import { Snackbar, Alert, Fade, Slide } from '@mui/material'
import { styled } from '@mui/system'
import { Typography, Dialog, DialogTitle, DialogContent, Button, Box, DialogActions, Stack, Divider, IconButton } from '@vapor/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { CookieOption } from './cookie-option'
import { TSCookies } from './cookie.types'
import { cookieVars } from './open-dialog-var'
import { useReactiveVar } from '@apollo/client'
import { Trans, useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose } from '@fortawesome/pro-solid-svg-icons'

const MyAlert = styled(Alert)(({ theme }) => ({
  '&.MuiAlert-outlinedWarning': {
    border: '1px solid ' + theme.palette.primary.baseWarningColor,
    borderBottom: '4px solid ' + theme.palette.primary.baseWarningColor,
    '& .MuiAlert-icon': {
      color: theme.palette.primary.baseWarningColor,
    },
  },
}))

const COOKIE_NAME = 'tsh_cookie_choices'
const COOKIE_EXPIRATON_MS = 180 * 24 * 60 * 60 * 1000 //GG * h * m * s * ms
const COOKIE_PATH = '/'

export const CookiesSelector: FC = (props) => {
  const { t } = useTranslation()
  const [openBanner, setOpenBanner] = useState<boolean>(false)
  const [cookieSettingsTemp, setCookieSettingsTemp] = useState<{ [key in TSCookies]?: boolean }>({})
  const [cookieSettings, setCookieSettings] = useState<{ [key in TSCookies]?: boolean }>({})

  const setDefaultCookiePreference = () => {
    const defaultSettings = { C0001: true, C0002: true, C0005: false }
    setCookieSettings(defaultSettings)
  }

  const readCookie = () => {
    const ourCookie = COOKIE_NAME + '='
    const cookie = document.cookie.split(';')?.find((c) => c.trim().startsWith(ourCookie))
    if (cookie) {
      const settings = decodeURIComponent(cookie.trim().substring(ourCookie.length, cookie.length))
      setCookieSettings(JSON.parse(settings))
      return true
    }
    return false
  }

  useEffect(() => {
    setDefaultCookiePreference()
    if (!readCookie()) {
      setOpenBanner(true)
    }
  }, [])

  const createCookie = useCallback(
    (externalSettings?: { [key in TSCookies]?: boolean }) => {
      let expires = undefined

      const date = new Date()
      date.setTime(date.getTime() + COOKIE_EXPIRATON_MS)
      expires = '; expires=' + date.toUTCString()
      const pathDirective = '; path=' + COOKIE_PATH

      document.cookie = encodeURIComponent(COOKIE_NAME) + '=' + encodeURIComponent(JSON.stringify(externalSettings ?? cookieSettings)) + expires + pathDirective
    },
    [cookieSettings],
  )

  const onChange = (cookieId: TSCookies, value: boolean) => {
    setCookieSettings((c) => ({ ...c, [cookieId]: value }))
  }

  const openDialogHandler = () => {
    cookieVars({ ...cookieVars(), openCookieDialog: true })
    setOpenBanner(false)
  }

  const acceptAllHandler = () => {
    const updSettings = { ...cookieSettings, C0005: true }
    setCookieSettings(updSettings)
    createCookie(updSettings)
    setOpenBanner(false)
  }

  const onSaveHandler = () => {
    createCookie()
    cookieVars({ ...cookieVars(), openCookieDialog: false })
    setOpenBanner(false)
  }

  const onSkipHandler = useCallback(() => {
    setOpenBanner(false)
    cookieVars({ ...cookieVars(), openCookieDialog: false })
  }, [cookieVars()])

  const closeDialogWithoutSave = () => {
    setCookieSettings(cookieSettingsTemp)
    cookieVars({ openCookieDialog: false, cookieSettings: cookieSettingsTemp })
  }

  const openCookieDialog = useReactiveVar(cookieVars)

  useEffect(() => {
    cookieVars({ ...cookieVars(), cookieSettings: cookieSettings })
  }, [cookieSettings])

  useEffect(() => {
    console.log(openCookieDialog.openCookieDialog)
    if (openCookieDialog.openCookieDialog) {
      setCookieSettingsTemp({ ...cookieSettings })
    }
  }, [openCookieDialog.openCookieDialog])

  //#region snippets
  const cookieAlertText = (
    <div>
      {t('pms.commons.cookies.alert_text')}{' '}
      <a href="https://docs.teamsystem.com/Teamsystem%20Exp%20Cookie%20Policy.html" target="blank">
        {t('commons.discover_more')}
      </a>
    </div>
  )

  const cookieDialogSummary = (
    <p>
      {t('pms.commons.cookies.dialog_summary')}{' '}
      <a href="https://docs.teamsystem.com/Teamsystem%20Exp%20Cookie%20Policy.html" target="blank">
        {t('commons.discover_more')}
      </a>
    </p>
  )

  const C0001Description = <p>{t('pms.commons.cookies.c0001_description')}</p>
  const C0002Description = <p>{t('pms.commons.cookies.c0002_description')}</p>

  const C0005Description = (
    <span>
      <p>{t('pms.commons.cookies.c0005_description.p1')}</p>
      <ul>
        <li>{t('pms.commons.cookies.c0005_description.li1')}</li>
        <li>{t('pms.commons.cookies.c0005_description.li2')}</li>
      </ul>
      <p>{t('pms.commons.cookies.c0005_description.p2')}</p>
      <p>{t('pms.commons.cookies.c0005_description.p3')}</p>
    </span>
  )
  //#endregion

  return (
    <>
      <>{props.children}</>
      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ width: { sm: '90%', md: '70%' } }} open={openBanner} transitionDuration={1000} TransitionComponent={Slide}>
        <MyAlert variant="outlined" severity="warning">
          <Typography variant="body">{cookieAlertText}</Typography>
          <Stack direction="row" sx={{ mt: 2, width: '100%', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={() => onSkipHandler()}>
                {t('pms.commons.cookies.continue_without')}
              </Button>
              <Button variant="outlined" size="small" onClick={() => openDialogHandler()}>
                {t('pms.commons.cookies.customize')}
              </Button>
            </Stack>
            <Button variant="outlined" size="small" onClick={() => acceptAllHandler()}>
              {t('pms.commons.cookies.accept_all')}
            </Button>
          </Stack>
        </MyAlert>
      </Snackbar>
      <Dialog open={openCookieDialog.openCookieDialog} onClose={() => closeDialogWithoutSave()} fullWidth maxWidth="sm" sx={{ '& .MuiDialog-paper': { height: 500, maxHeight: 500 } }}>
        <DialogTitle>
          <Stack direction="row" alignItems={'center'} width={'100%'}>
            {t('pms.commons.cookies.dialog_title')}
            <IconButton edge="end" size="small" sx={{ ml: 'auto' }} onClick={closeDialogWithoutSave}>
              <FontAwesomeIcon icon={faClose} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ m: 0, p: '0px !important' }}>
          <Divider />
          <Box sx={{ px: 4, textAlign: 'justify' }}>
            <Typography variant="body">{cookieDialogSummary}</Typography>
          </Box>
          <Box sx={{ p: 1, textAlign: 'justify' }}>
            <CookieOption value={cookieSettings.C0001} title={t('pms.commons.cookies.c0001_title')} description={C0001Description} mandatory onChange={(v) => onChange('C0001', v)}></CookieOption>
            <CookieOption value={cookieSettings.C0002} title={t('pms.commons.cookies.c0002_title')} description={C0002Description} mandatory onChange={(v) => onChange('C0002', v)}></CookieOption>
            <CookieOption value={cookieSettings.C0005} title={t('pms.commons.cookies.c0005_title')} description={C0005Description} onChange={(v) => onChange('C0005', v)}></CookieOption>
          </Box>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" sx={{ width: '100%', justifyContent: 'space-between' }}>
            <Stack direction={'row'} spacing={1}>
              <Button variant="outlined" size="small" onClick={() => onSkipHandler()}>
                {t('pms.commons.cookies.continue_without')}
              </Button>
            </Stack>
            <Button variant="outlined" size="small" onClick={(e) => onSaveHandler()}>
              {t('commons.save')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  )
}
